import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../stores/hooks/hooks.ts";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { signInUser } from "../../../stores/thunks/userAuthThunk.tsx";
import { UserSignInBodyType } from "../../../stores/types/userAuthTypes.tsx";
import { RevButton, RevDialog, RevInput, RevLink } from "@revmatics/catalyst";
import Logo from "/images/logo.svg";
import { displayAlert } from "../../../stores/slice/alertSlice.tsx";

type SignInFormData = UserSignInBodyType;

export const SignInPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm<SignInFormData>({
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onFormSubmit: SubmitHandler<SignInFormData> = (data) => {
    clearErrors("root.serverError");
    setIsSubmitting(true);

    dispatch(signInUser(data))
      .unwrap()
      .then((response) => {
        if (response.enable_quickstart && !response.has_completed_onboarding) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/personas", { replace: true });
        }
      })
      .catch((rejectedValue) => {
        console.error(
          "[SignInPage] Dispatch rejected (Sign in failed):",
          rejectedValue,
        );

        let serverErrorMessage = "Sign in failed. Please try again.";
        if (typeof rejectedValue === "string" && rejectedValue.length > 0) {
          serverErrorMessage = rejectedValue;
        }

        setError("root.serverError", {
          type: "server",
          message: serverErrorMessage,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    const authError = sessionStorage.getItem("expired_err");
    if (authError) {
      const { heading, text } = JSON.parse(authError);
      dispatch(displayAlert({ type: "error", heading: heading, text: text }));
      sessionStorage.removeItem("expired_err");
    }
  }, [dispatch]);

  const infoDialogActions = [
    <RevButton
      key="okay"
      text="Okay"
      variant="primary"
      handleClick={() => setIsInfoDialogOpen(false)}
    />,
  ];

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="w-full space-y-4"
          noValidate
        >
          <div className="flex justify-start">
            <img src={Logo} alt="Company Logo" className="h-[35px] w-auto" />
          </div>
          <h3>Login</h3>
          <div className="flex items-baseline gap-2">
            <p className="typography-body-sm text-monochrome-900">
              Need an account?
            </p>
            <RevButton
              linkFontSize="small"
              text="Create an account"
              displayAs="link"
              handleClick={() => setIsInfoDialogOpen(true)}
              underline={true}
            />
          </div>

          <RevInput
            name="email"
            label="Business email"
            type="email"
            placeholder="Your business email"
            size="md"
            required
            register={register("email", {
              required: "Business email is required.",
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: "Entered value does not match email format.",
              },
            })}
            error={errors.email}
            enableClearButton={true}
            onClear={() => setValue("email", "", { shouldValidate: true })}
          />

          <RevInput
            name="password"
            label="Password"
            type="password"
            placeholder="Your password"
            size="md"
            required
            className="mb-0"
            register={register("password", {
              required: "Password is required.",
            })}
            error={errors.password}
            enableClearButton={true}
            onClear={() => setValue("password", "", { shouldValidate: true })}
          />

          {errors.root?.serverError && (
            <div className="text-left">
              <p
                className="text-xs text-red-500"
                aria-label="Server Error Message"
              >
                {errors.root.serverError.message}
              </p>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <RevLink
              linkFontSize="medium"
              text="Forgot password"
              pathname="/forgot-password"
              underline={true}
            />
          </div>
          <RevButton
            type="submit"
            variant="primary"
            text="Sign In"
            className="w-full"
            isLoading={isSubmitting}
            displayAs="button"
          />
        </form>
      </div>
      <RevDialog
        isOpen={isInfoDialogOpen}
        setIsOpen={setIsInfoDialogOpen}
        variant="info"
        title="Create an Account"
        btnList={infoDialogActions}
      >
        <div className="typography-body-sm text-monochrome-900 text-center">
          In order to create an account, please contact{" "}
          <a
            href="mailto:support@revmatics.ai"
            className="underline hover:no-underline"
          >
            support@revmatics.ai
          </a>{" "}
          so that a Revmatics specialist can help you.
        </div>
      </RevDialog>
    </>
  );
};
